import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Typography, Tag, Row, Col, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { customFetch } from '../utils/api';
import debounce from 'lodash.debounce';

const { Title } = Typography;
const { Search } = Input;

interface User {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_staff: boolean;
    date_joined: string;
}

interface UserState {
    users: User[];
    pagination: {
        current: number;
        pageSize: number;
        total: number;
    };
    searchTerm: string;
}

const columns: ColumnsType<User> = [
    { title: 'Username', dataIndex: 'username', key: 'username' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Name', key: 'name', render: (_, record) => `${record.first_name || ''} ${record.last_name || ''}`.trim() },
    { title: 'Staff Status', dataIndex: 'is_staff', key: 'is_staff', render: (is_staff: boolean) => (<Tag color={is_staff ? 'green' : 'volcano'}>{is_staff ? 'STAFF' : 'USER'}</Tag>) },
    { title: 'Date Joined', dataIndex: 'date_joined', key: 'date_joined', render: (date: string) => new Date(date).toLocaleDateString() },
];

const UserManagementPage: React.FC = () => {
    const [state, setState] = useState<UserState>({
        users: [],
        pagination: { current: 1, pageSize: 10, total: 0 },
        searchTerm: '',
    });
    const [loading, setLoading] = useState(false);

    const fetchUsers = useCallback((page: number, pageSize: number, search: string) => {
        setLoading(true);
        const params = new URLSearchParams({
            page: String(page),
            page_size: String(pageSize),
        });
        if (search) {
            params.append('search', search);
        }

        customFetch(`/api/users/?${params.toString()}`)
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok');
                return response.json();
            })
            .then(data => {
                setState(prevState => ({
                    ...prevState,
                    users: data.results,
                    pagination: { ...prevState.pagination, total: data.count },
                }));
            })
            .catch(() => message.error('Could not load users.'))
            .finally(() => setLoading(false));
    }, []);
    
    useEffect(() => {
        fetchUsers(state.pagination.current, state.pagination.pageSize, state.searchTerm);
    }, [state.pagination.current, state.pagination.pageSize, state.searchTerm, fetchUsers]);
    
    const debouncedSearch = useCallback(debounce((value: string) => {
        setState(s => ({ ...s, searchTerm: value, pagination: { ...s.pagination, current: 1 } }));
    }, 300), []);

    const handleTableChange = (p: any) => {
        setState(s => ({ ...s, pagination: {...s.pagination, current: p.current, pageSize: p.pageSize }}));
    };

    return (
        <div>
            <Title level={2}>User Management</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col>
                    <Search
                        placeholder="Search by username or email"
                        onChange={(e) => debouncedSearch(e.target.value)}
                        style={{ width: 300 }}
                        allowClear
                    />
                </Col>
            </Row>
            <Table
                columns={columns}
                dataSource={state.users}
                rowKey="id"
                loading={loading}
                pagination={state.pagination}
                onChange={handleTableChange}
            />
        </div>
    );
};

export default UserManagementPage; 